import { getRoutePath } from '@/config/routes';
import type { StatsRange } from '@/types/stats';

/**
 * Statistics page settings: ranges, refresh intervals, data freshness and the
 * error-rate thresholds that highlight a site.
 */

interface StatsRangeSettings {
  /** Range selector label */
  label: string;
  /** e.g. "last 24 hours" */
  description: string;
  /** How often the page and the drill-down refresh */
  refreshIntervalMs: number;
  /** Whether the drill-down charts requests over time (a 1-minute range is a single bar) */
  hasTimeChart: boolean;
}

/**
 * Refresh of the short ranges: the control plane collects every 15 seconds
 */
const SHORT_RANGE_REFRESH_MS = 15_000;

/**
 * Refresh of the longer ranges: a minute changes them little
 */
const LONG_RANGE_REFRESH_MS = 60_000;

const RANGE_SETTINGS: Readonly<Record<StatsRange, StatsRangeSettings>> = {
  '1m': { label: '1m', description: 'last minute', refreshIntervalMs: SHORT_RANGE_REFRESH_MS, hasTimeChart: false },
  '15m': { label: '15m', description: 'last 15 minutes', refreshIntervalMs: SHORT_RANGE_REFRESH_MS, hasTimeChart: true },
  '1h': { label: '1h', description: 'last hour', refreshIntervalMs: LONG_RANGE_REFRESH_MS, hasTimeChart: true },
  '6h': { label: '6h', description: 'last 6 hours', refreshIntervalMs: LONG_RANGE_REFRESH_MS, hasTimeChart: true },
  '24h': { label: '24h', description: 'last 24 hours', refreshIntervalMs: LONG_RANGE_REFRESH_MS, hasTimeChart: true },
  '7d': { label: '7d', description: 'last 7 days', refreshIntervalMs: LONG_RANGE_REFRESH_MS, hasTimeChart: true },
  '30d': { label: '30d', description: 'last 30 days', refreshIntervalMs: LONG_RANGE_REFRESH_MS, hasTimeChart: true },
};

const RANGE_ORDER: readonly StatsRange[] = ['1m', '15m', '1h', '6h', '24h', '7d', '30d'];

/**
 * Range selector options, shortest first
 */
export const STATS_RANGES: ReadonlyArray<{ value: StatsRange; label: string }> = RANGE_ORDER.map((value) => ({
  value,
  label: RANGE_SETTINGS[value].label,
}));

const DEFAULT_STATS_RANGE: StatsRange = '24h';

/**
 * Parse a range from the URL; unknown values fall back to the default
 */
export function parseStatsRange(value: string | null | undefined): StatsRange {
  return STATS_RANGES.find((range) => range.value === value)?.value ?? DEFAULT_STATS_RANGE;
}

/**
 * "last 24 hours"
 */
export function describeStatsRange(range: StatsRange): string {
  return RANGE_SETTINGS[range].description;
}

/**
 * How often the statistics of a range refresh: every 15 seconds for the short
 * ranges, every minute otherwise
 */
export function getStatsRefreshInterval(range: StatsRange): number {
  return RANGE_SETTINGS[range].refreshIntervalMs;
}

/**
 * Whether the drill-down of a range shows the requests over time; without a
 * chart it shows the status classes of the range
 */
export function statsRangeHasTimeChart(range: StatsRange): boolean {
  return RANGE_SETTINGS[range].hasTimeChart;
}

/**
 * Statistics complete up to longer ago are flagged as delayed. Normally they
 * lag 15-90 seconds (the agents emit a minute 15 seconds after its end, the
 * control plane collects every 15 seconds); more means a node or the
 * collection is behind.
 */
const STATS_DELAYED_AFTER_MS = 3 * 60_000;

/**
 * Whether statistics complete up to `dataUntil` (the API's data_until) are
 * delayed at `now`
 */
export function isStatsDataDelayed(dataUntil: string, now: number = Date.now()): boolean {
  const until = Date.parse(dataUntil);
  return !Number.isNaN(until) && now - until > STATS_DELAYED_AFTER_MS;
}

/**
 * Severity of an error rate
 */
export type RateLevel = 'ok' | 'warning' | 'critical';

interface RateThresholds {
  /** Highlight as warning at or above this share (0..1) */
  warning: number;
  /** Highlight as critical at or above this share (0..1); omit for no critical level */
  critical?: number;
}

/**
 * 5xx share of a site's requests
 */
export const SERVER_ERROR_RATE_THRESHOLDS: RateThresholds = { warning: 0.01, critical: 0.05 };

/**
 * 4xx share of a site's requests (bots and stale links make some 4xx normal)
 */
export const CLIENT_ERROR_RATE_THRESHOLDS: RateThresholds = { warning: 0.2 };

function rateLevel(rate: number, thresholds: RateThresholds): RateLevel {
  if (thresholds.critical !== undefined && rate >= thresholds.critical) return 'critical';
  if (rate >= thresholds.warning) return 'warning';
  return 'ok';
}

/**
 * Level of a 5xx rate (error_rate)
 */
export function getServerErrorLevel(rate: number): RateLevel {
  return rateLevel(rate, SERVER_ERROR_RATE_THRESHOLDS);
}

/**
 * Level of a 4xx rate (client_error_rate)
 */
export function getClientErrorLevel(rate: number): RateLevel {
  return rateLevel(rate, CLIENT_ERROR_RATE_THRESHOLDS);
}

/**
 * Range of the dashboard's "HTTP errors" card
 */
export const RECENT_ERRORS_RANGE: StatsRange = '1h';

/**
 * Number of sites listed on the dashboard's "HTTP errors" card
 */
export const RECENT_ERRORS_LIMIT = 3;

/**
 * URL search parameters of the statistics page (filters and open drill-down)
 */
export const STATS_PARAMS = {
  range: 'range',
  node: 'node',
  domain: 'domain',
} as const;

/**
 * Link to the statistics page, optionally with a domain's drill-down open,
 * e.g. "/stats?range=1h&domain=example.com"
 */
export function statsLink({ range, domain }: { range: StatsRange; domain?: string }): string {
  const params = new URLSearchParams({ [STATS_PARAMS.range]: range });
  if (domain) params.set(STATS_PARAMS.domain, domain);
  return `${getRoutePath('stats')}?${params.toString()}`;
}
